import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
  PieChart,
  Pie,
  Legend,
} from "recharts";
import { ArrowLeft, Package, Truck, CheckCircle, CalendarBlank } from "@phosphor-icons/react";

const PALETTE = ["#F97316", "#3B82F6", "#10B981", "#8B5CF6", "#F59E0B", "#EF4444"];

export default function MonthlyReport() {
  const { month } = useParams();
  const [report, setReport] = useState(null);

  useEffect(() => {
    api
      .getMonthReport(month)
      .then(setReport)
      .catch(() => toast.error("Failed to load monthly report"));
  }, [month]);

  if (!report) {
    return (
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center text-slate-500" data-testid="monthly-loading">
        Loading monthly report...
      </div>
    );
  }

  const share = report.warehouses
    .filter((w) => w.received > 0)
    .map((w) => ({ name: w.warehouse.name, value: w.received }));
  const dailyChart = report.daily.map((d) => ({
    date: d.date.slice(8),
    Doral: d.doral_received,
    Medley: d.medley_received,
  }));

  return (
    <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6" data-testid="monthly-report-page">
      <div>
        <Link
          to="/archives"
          data-testid="monthly-back-to-archives"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-700 transition-colors duration-200"
        >
          <ArrowLeft size={16} weight="bold" /> Archives
        </Link>
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 mt-1">
          Monthly Report
        </h1>
        <p className="text-base md:text-lg text-slate-500 mt-1 flex items-center gap-2 flex-wrap" data-testid="monthly-label">
          {report.label}
          {report.excluded_demo_dates.length > 0 && (
            <span
              className="inline-flex items-center text-xs font-bold uppercase tracking-wider text-amber-700 bg-amber-100 border border-amber-300 rounded px-2 py-0.5"
              data-testid="monthly-demo-note"
            >
              Demo report excluded ({report.excluded_demo_dates.join(", ")})
            </span>
          )}
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="monthly-summary">
        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-4">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
            <Package size={14} weight="duotone" /> Received
          </div>
          <div className="font-heading text-3xl font-extrabold text-orange-500 mt-1" data-testid="monthly-grand-received">
            {report.grand_received}
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-4">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
            <CheckCircle size={14} weight="duotone" /> Processed
          </div>
          <div className="font-heading text-3xl font-extrabold text-emerald-600 mt-1" data-testid="monthly-grand-processed">
            {report.grand_processed}
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-4">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
            <Truck size={14} weight="duotone" /> Deliveries
          </div>
          <div className="font-heading text-3xl font-extrabold text-slate-900 mt-1" data-testid="monthly-deliveries">
            {report.total_deliveries}
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-4">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
            <CalendarBlank size={14} weight="duotone" /> Days Active
          </div>
          <div className="font-heading text-3xl font-extrabold text-slate-900 mt-1" data-testid="monthly-days">
            {report.days_active}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="monthly-charts">
        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-5">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-4">
            Packages received per day
          </div>
          <div className="h-64" data-testid="chart-monthly-daily">
            {dailyChart.length === 0 ? (
              <div className="h-full flex items-center justify-center text-slate-400 text-sm">No data this month</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dailyChart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748B" }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#64748B" }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="Doral" fill="#F97316" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="Medley" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-md shadow-sm p-5">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-4">
            Monthly share by warehouse
          </div>
          <div className="h-64" data-testid="chart-monthly-share">
            {share.length === 0 ? (
              <div className="h-full flex items-center justify-center text-slate-400 text-sm">No packages received this month</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={share} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={3}>
                    {share.map((_, i) => (
                      <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {report.warehouses.map((w) => {
          const carrierData = Object.entries(w.carrier_totals).map(([carrier, packages]) => ({ carrier, packages }));
          return (
            <section
              key={w.warehouse.id}
              className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden"
              data-testid={`monthly-section-${w.warehouse.id}`}
            >
              <div className="px-5 py-4 border-b border-slate-200">
                <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">{w.warehouse.name}</h2>
                <div className="text-sm text-slate-500" data-testid={`monthly-sub-${w.warehouse.id}`}>
                  {w.delivery_count} {w.delivery_count === 1 ? "delivery" : "deliveries"} · {w.received} received · {w.processed} processed
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-0">
                <div className="overflow-x-auto">
                  {carrierData.length === 0 ? (
                    <div className="p-8 text-center text-slate-400 text-sm">No deliveries this month.</div>
                  ) : (
                    <table className="w-full text-sm text-left text-slate-600" data-testid={`monthly-carrier-table-${w.warehouse.id}`}>
                      <thead className="text-xs uppercase bg-slate-50 text-slate-500 border-b border-slate-200 font-semibold tracking-wider">
                        <tr>
                          <th className="px-5 py-3">Carrier</th>
                          <th className="px-5 py-3">Packages</th>
                        </tr>
                      </thead>
                      <tbody>
                        {carrierData.map((c) => (
                          <tr key={c.carrier} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                            <td className="px-5 py-2.5">
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200">
                                {c.carrier}
                              </span>
                            </td>
                            <td className="px-5 py-2.5 font-semibold text-slate-900">{c.packages}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-orange-50 border-t border-orange-200">
                          <td className="px-5 py-3 font-bold text-slate-900">TOTAL RECEIVED</td>
                          <td className="px-5 py-3 font-bold text-orange-600" data-testid={`monthly-total-${w.warehouse.id}`}>{w.received}</td>
                        </tr>
                        <tr className="bg-emerald-50 border-t border-emerald-100">
                          <td className="px-5 py-3 font-bold text-slate-900">PROCESSED</td>
                          <td className="px-5 py-3 font-bold text-emerald-600" data-testid={`monthly-processed-${w.warehouse.id}`}>{w.processed}</td>
                        </tr>
                      </tfoot>
                    </table>
                  )}
                </div>
                <div className="border-t sm:border-t-0 sm:border-l border-slate-200 p-5">
                  <div className="h-56" data-testid={`chart-monthly-carrier-${w.warehouse.id}`}>
                    {carrierData.length === 0 ? (
                      <div className="h-full flex items-center justify-center text-slate-400 text-sm">No data</div>
                    ) : (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={carrierData} layout="vertical" margin={{ left: 16 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                          <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: "#64748B" }} />
                          <YAxis type="category" dataKey="carrier" width={96} tick={{ fontSize: 11, fill: "#334155" }} />
                          <Tooltip />
                          <Bar dataKey="packages" radius={[0, 4, 4, 0]}>
                            {carrierData.map((_, i) => (
                              <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </div>
              </div>
            </section>
          );
        })}
      </div>

      <section className="bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden" data-testid="monthly-daily-section">
        <div className="px-5 py-4 border-b border-slate-200">
          <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Daily breakdown</h2>
        </div>
        {report.daily.length === 0 ? (
          <div className="p-8 text-center text-slate-400 text-sm" data-testid="monthly-daily-empty">No activity this month.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left text-slate-600" data-testid="monthly-daily-table">
              <thead className="text-xs uppercase bg-slate-50 text-slate-500 border-b border-slate-200 font-semibold tracking-wider">
                <tr>
                  <th className="px-5 py-3">Date</th>
                  <th className="px-5 py-3">Doral received</th>
                  <th className="px-5 py-3">Doral processed</th>
                  <th className="px-5 py-3">Medley received</th>
                  <th className="px-5 py-3">Medley processed</th>
                  <th className="px-5 py-3">Day total</th>
                </tr>
              </thead>
              <tbody>
                {report.daily.map((d) => (
                  <tr key={d.date} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`monthly-day-${d.date}`}>
                    <td className="px-5 py-2.5 font-semibold text-slate-900">{d.date}</td>
                    <td className="px-5 py-2.5">{d.doral_received}</td>
                    <td className="px-5 py-2.5 text-emerald-600">{d.doral_processed}</td>
                    <td className="px-5 py-2.5">{d.medley_received}</td>
                    <td className="px-5 py-2.5 text-emerald-600">{d.medley_processed}</td>
                    <td className="px-5 py-2.5 font-semibold text-orange-600">{d.total}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-orange-50 border-t border-orange-200">
                  <td className="px-5 py-3 font-bold text-slate-900">MONTH TOTAL</td>
                  <td className="px-5 py-3 font-bold text-slate-900">{report.warehouses.find((w) => w.warehouse.id === "doral")?.received ?? 0}</td>
                  <td className="px-5 py-3 font-bold text-emerald-600">{report.warehouses.find((w) => w.warehouse.id === "doral")?.processed ?? 0}</td>
                  <td className="px-5 py-3 font-bold text-slate-900">{report.warehouses.find((w) => w.warehouse.id === "medley")?.received ?? 0}</td>
                  <td className="px-5 py-3 font-bold text-emerald-600">{report.warehouses.find((w) => w.warehouse.id === "medley")?.processed ?? 0}</td>
                  <td className="px-5 py-3 font-bold text-orange-600" data-testid="monthly-daily-grand">{report.grand_received}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </section>
    </main>
  );
}
